import { rem } from 'polished'
import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { LoyaltyLandingPageBenefitDetails, getLoyaltyLandingPageBenefitCopy } from '../LoyaltyLandingPageData'
import LoyaltyLandingPageSmallTile from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageSmallTile'
import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Pane from 'components/Common/Pane'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import Heading from 'LuxKit/Typography/Heading'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Plural from 'components/utils/Formatters/Plural'
import { useLocation } from 'react-router'

const NextTierContent = styled(Pane)`
  padding: ${rem(24)} ${rem(20)};
   /* Offset for sticky header */
  scroll-margin-top: 80px;

  ${mediaQueryUp.tablet} {
    padding: ${rem(28)} ${rem(24)};
  }
`

const SmallTilesSection = styled.div`
  display: grid;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

function getNextTierBenefits(
  enabledBenefits: App.LoyaltyRegionEnabledBenefits,
  regionTierBenefits: App.LoyaltyTierRegionEnabledBenefits,
): Record<App.LoyaltyTier, Array<LoyaltyLandingPageBenefitDetails>> {
  const loyaltyLandingPageBenefitTiles = getLoyaltyLandingPageBenefitCopy(enabledBenefits)

  const silverLoungePassBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'lounge_pass')
  const goldLoungePassBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'lounge_pass')
  const platinumLoungePassBenefit = regionTierBenefits.platinum.find(benefit => benefit.code === 'lounge_pass')

  const silverHotelUpgradeBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'hotel_upgrade')
  const goldHotelUpgradeBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'hotel_upgrade')
  const platinumHotelUpgradeBenefit = regionTierBenefits.platinum.find(benefit => benefit.code === 'hotel_upgrade')

  const goldAirportTransferBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'airport_transfer')
  const platinumAirportTransferBenefit = regionTierBenefits.platinum.find(benefit => benefit.code === 'airport_transfer')

  const silverDiscountedInsuranceBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'discounted_insurance')
  const goldDiscountedInsuranceBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'discounted_insurance')
  const platinumDiscountedInsuranceBenefit = regionTierBenefits.platinum.find(benefit => benefit.code === 'discounted_insurance')

  const benefits = {
    bronze: [],
    silver: [
      {
        title: loyaltyLandingPageBenefitTiles.lounge_pass?.title,
        description: <>Enjoy <b>{silverLoungePassBenefit?.unlimited ? 'unlimited' : silverLoungePassBenefit?.limit} lounge <Plural count={silverLoungePassBenefit?.limit ?? 0} singular="pass" plural="passes" /></b> to use for you and your travel companion, or keep them both for yourself.</>,
        image: loyaltyLandingPageBenefitTiles.lounge_pass!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.lounge_pass!.benefitCode,
        enabled: enabledBenefits.lounge_pass,
      },
      {
        title: loyaltyLandingPageBenefitTiles.hotel_upgrade?.title,
        description: <><b>{silverHotelUpgradeBenefit?.unlimited ? 'unlimited' : silverHotelUpgradeBenefit?.limit} free room or package <Plural count={silverHotelUpgradeBenefit?.limit ?? 0} singular="upgrade" /></b> on select offers each membership year.</>,
        image: loyaltyLandingPageBenefitTiles.hotel_upgrade!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.hotel_upgrade!.benefitCode,
        enabled: enabledBenefits.hotel_upgrade,
      },
      {
        title: loyaltyLandingPageBenefitTiles.phone_support?.title,
        description: <><b>Skip the queue with 24/7 access</b> to our dedicated support team</>,
        image: loyaltyLandingPageBenefitTiles.phone_support!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.phone_support!.benefitCode,
        enabled: enabledBenefits.phone_support,
      },
    ].filter(benefit => benefit.enabled),
    gold: [
      {
        title: loyaltyLandingPageBenefitTiles.lounge_pass?.title,
        description: <>
          {goldLoungePassBenefit?.unlimited && <>Enjoy <b>unlimited</b> lounge passes to use for you and your travel companion, or keep them both for yourself.</>}
          {!goldLoungePassBenefit?.unlimited && <>Enjoy an additional <b>{goldLoungePassBenefit?.limit} lounge <Plural count={goldLoungePassBenefit?.limit ?? 0} singular="pass" plural="passes" /></b> to use for you and your travel companion, or keep them both for yourself.</>}
        </>,
        image: loyaltyLandingPageBenefitTiles.lounge_pass!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.lounge_pass!.benefitCode,
        enabled: enabledBenefits.lounge_pass,
      },
      {
        title: <>More upgrades to enjoy</>,
        description: <>
          {goldHotelUpgradeBenefit?.unlimited && <>Enjoy <b>unlimited</b> room or package upgrades on select offers each membership year.</>}
          {!goldHotelUpgradeBenefit?.unlimited && <>Enjoy an additional <b>{goldHotelUpgradeBenefit?.limit} room or package <Plural count={goldHotelUpgradeBenefit?.limit ?? 0} singular="upgrade" /></b> on select offers each membership year.</>}
        </>,
        image: loyaltyLandingPageBenefitTiles.hotel_upgrade!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.hotel_upgrade!.benefitCode,
        enabled: enabledBenefits.hotel_upgrade,
      },
      {
        title: loyaltyLandingPageBenefitTiles.airport_transfer?.title,
        description: <>
          {goldAirportTransferBenefit?.unlimited && <>Enjoy <b>unlimited</b> private chauffeur transfers from in select destinations, every Gold year.</>}
          {!goldAirportTransferBenefit?.unlimited && <><b>{goldAirportTransferBenefit?.limit} credit</b> private chauffeur transfers from in select destinations, every Gold year.</>}
        </>,
        image: loyaltyLandingPageBenefitTiles.airport_transfer!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.airport_transfer!.benefitCode,
        enabled: enabledBenefits.airport_transfer,
      },
    ].filter(benefit => benefit.enabled),
    platinum: [
      {
        title: loyaltyLandingPageBenefitTiles.airport_transfer?.title,
        description: <>
          {platinumAirportTransferBenefit?.unlimited && <><b>Unlimited</b> private chauffeur transfers from in select destinations, every Platinum year.</>}
          {!platinumAirportTransferBenefit?.unlimited && <><b>{platinumAirportTransferBenefit?.limit} credit</b> private chauffeur transfers from in select destinations, every Platinum year.</>}
        </>,
        image: loyaltyLandingPageBenefitTiles.airport_transfer!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.airport_transfer!.benefitCode,
        enabled: enabledBenefits.airport_transfer,
      },
      {
        title: <>More upgrades to enjoy</>,
        description: <>
          {platinumHotelUpgradeBenefit?.unlimited && <>Enjoy <b>unlimited</b> room or package upgrades on select offers each membership year.</>}
          {!platinumHotelUpgradeBenefit?.unlimited && <><b>Enjoy an additional {platinumHotelUpgradeBenefit?.limit} room or package <Plural count={platinumHotelUpgradeBenefit?.limit ?? 0} singular="upgrade" /></b> on select offers each membership year.</>}
        </>,
        image: loyaltyLandingPageBenefitTiles.hotel_upgrade!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.hotel_upgrade!.benefitCode,
        enabled: enabledBenefits.hotel_upgrade,
      },
      {
        title: loyaltyLandingPageBenefitTiles.lounge_pass?.title,
        description: <>
          {platinumLoungePassBenefit?.unlimited && <>Enjoy <b>unlimited</b> lounge passes to use for you and your travel companion, or keep them both for yourself.</>}
          {!platinumLoungePassBenefit?.unlimited && <>Enjoy an additional <b>{platinumLoungePassBenefit?.limit} lounge <Plural count={platinumLoungePassBenefit?.limit ?? 0} singular="pass" plural="passes" /></b> to use for you and your travel companion, or keep them both for yourself.</>}
        </>,
        image: loyaltyLandingPageBenefitTiles.lounge_pass!.image,
        benefitCode: loyaltyLandingPageBenefitTiles.lounge_pass!.benefitCode,
        enabled: enabledBenefits.lounge_pass,
      },
    ].filter(benefit => benefit.enabled),
  }

  if (!enabledBenefits.lounge_pass && enabledBenefits.discounted_insurance) {
    benefits.silver.push({
      title: loyaltyLandingPageBenefitTiles.discounted_insurance!.title,
      description: <><b>Save {silverDiscountedInsuranceBenefit?.value?.amount ?? 0}%</b> on travel & cancellation protection</>,
      image: loyaltyLandingPageBenefitTiles.discounted_insurance!.image,
      benefitCode: loyaltyLandingPageBenefitTiles.discounted_insurance!.benefitCode,
      enabled: enabledBenefits.discounted_insurance,
    })
    benefits.gold.push({
      title: loyaltyLandingPageBenefitTiles.discounted_insurance!.title,
      description: <><b>Save {goldDiscountedInsuranceBenefit?.value?.amount ?? 0}%</b> on travel & cancellation protection</>,
      image: loyaltyLandingPageBenefitTiles.discounted_insurance!.image,
      benefitCode: loyaltyLandingPageBenefitTiles.discounted_insurance!.benefitCode,
      enabled: enabledBenefits.discounted_insurance,
    })
    benefits.platinum.push({
      title: loyaltyLandingPageBenefitTiles.discounted_insurance!.title,
      description: <><b>Save {platinumDiscountedInsuranceBenefit?.value?.amount ?? 0}%</b> on travel & cancellation protection</>,
      image: loyaltyLandingPageBenefitTiles.discounted_insurance!.image,
      benefitCode: loyaltyLandingPageBenefitTiles.discounted_insurance!.benefitCode,
      enabled: enabledBenefits.discounted_insurance,
    })
  }

  return benefits
}

interface Props {
  /**
   * The next tier to display the benefits for whether that is the account tier or eligible tier
   * for users not enrolled
   */
  nextTier: App.LoyaltyTier
}

function LoyaltyLandingPageNextTierBenefitsContent({ nextTier }: Props) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const upgradesPromo = useLabFeatureFlag(LabFeatureFlags.enableLoyaltyUpgradesPromo)
  const BENEFITS_PROMOTIONS: Utils.PartialRecord<
    App.LoyaltyTier,
    Utils.PartialRecord<App.LoyaltyBenefitType, { promotion?: App.LabFeatureFlagResult }>
  > = useMemo(() => ({
    silver: {
      hotel_upgrade: { promotion: upgradesPromo },
    },
    gold: {
      hotel_upgrade: { promotion: upgradesPromo },
    },
  }), [upgradesPromo])

  const [config, configFetching, configError] = useLoyaltyConfig()
  const nextTierBenefits = useMemo(() => {
    if (!config) return
    return getNextTierBenefits(config.regionEnabledBenefits, config.regionTierBenefits)
  }, [config])

  useEffect(() => {
    if (location.hash === '#benefits-your-benefits' && sectionRef.current) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [location.hash])

  if (configFetching) return <LoadingIndicator />
  if (!config || !nextTierBenefits || configError) return null

  return <NextTierContent ref={sectionRef} id="benefits-your-benefits" background={`tier-${nextTier}-primary`}>
    <VerticalSpacer gap={20}>
      <Heading variant="heading4">
        Unlock {getLoyaltyTierDisplayName(nextTier)} and get access to these benefits
      </Heading>
      <SmallTilesSection>
        {nextTierBenefits[nextTier].map(benefit => <LoyaltyLandingPageSmallTile
          key={benefit.image.id}
          tile={benefit}
          promotion={BENEFITS_PROMOTIONS[nextTier]?.[benefit.benefitCode]?.promotion}
        />)}
      </SmallTilesSection>
    </VerticalSpacer>
  </NextTierContent>
}

export default LoyaltyLandingPageNextTierBenefitsContent
